import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Clock, Loader2, XCircle } from "lucide-react";
import {
  queueEntryKey,
  type GroomingQueueActionResult,
  type GroomingQueueEntry,
  type GroomingQueueEntryKind,
  type GroomingQueueState,
} from "@ttc-spectree/toolbox-contract";
import { RUN_PHASE_LABELS } from "@/components/grooming/run-activity";
import { useGroomingQueue } from "@/hooks/use-grooming-queue";
import { GROOMING_QUEUE_PATH } from "@/lib/grooming/paths";
import { useRunnerBridge } from "@/lib/grooming/runner-bridge";
import { FINISHED_TEXT } from "@/lib/grooming/plain-words";
import { showGroomingToast } from "@/lib/grooming/toasts";
import { cn } from "@/lib/utils";

/**
 * Queue status on the item pages (epic PI-SPEC-SW-E284, feature
 * PI-SPEC-SW-1440): where a tracker item, an author-questions draft or a
 * product-areas draft stands in SpecTree Toolbox's run queue on this
 * computer. "Queued · #3", "Running · Reading the code", or for a few
 * minutes after it finished "Finished" for a brief (E289: it makes no claim
 * about posting, since the brief state chip beside it says that), "Questions
 * drafted", "Draft ready" or "Failed". Clicking it opens the Queue screen.
 *
 * Local state only: the badge reads the app's queue (`useGroomingQueue`),
 * so it is absent in a browser and with a contract-2 app. The server's
 * "Being prepared by <person>" marker is a separate component
 * (`preparing-marker.tsx`).
 */

/** How long a finished entry still shows as finished ("Finished", a drafted state) or "Failed" on its item. */
export const RECENTLY_FINISHED_MS = 10 * 60_000;

/** What the badge says. */
export type QueueBadgeStatus =
  | { kind: "queued"; position: number; entry: GroomingQueueEntry }
  | { kind: "running"; entry: GroomingQueueEntry }
  | { kind: "ready"; entry: GroomingQueueEntry }
  | { kind: "failed"; entry: GroomingQueueEntry };

/** A queue entry key, or null when the key cannot be built (no item id). */
export function safeQueueKey(kind: GroomingQueueEntryKind, project: string, itemId?: string | null): string | null {
  if (project.trim() === "") return null;
  try {
    return queueEntryKey(kind, project, itemId ?? undefined);
  } catch {
    return null;
  }
}

/** The running or queued entry with this key, with its 1-based place in Up next. Pure; exported for tests. */
export function activeEntryByKey(
  state: GroomingQueueState | null,
  key: string | null
): Extract<QueueBadgeStatus, { kind: "queued" | "running" }> | null {
  if (!state || !key) return null;
  const running = state.running.find((entry) => entry.key === key);
  if (running) return { kind: "running", entry: running };
  const index = state.upNext.findIndex((entry) => entry.key === key);
  const queued = index >= 0 ? state.upNext[index] : undefined;
  return queued ? { kind: "queued", position: index + 1, entry: queued } : null;
}

/**
 * The badge for one key: running or queued, else an entry that finished
 * `briefed`/`done` or `failed` less than {@link RECENTLY_FINISHED_MS} ago,
 * else null. Pure; exported for tests.
 */
export function queueBadgeStatus(state: GroomingQueueState | null, key: string | null, now: number): QueueBadgeStatus | null {
  const active = activeEntryByKey(state, key);
  if (active) return active;
  if (!state || !key) return null;
  const finished = state.completed.find((entry) => entry.key === key);
  if (!finished?.finishedAt) return null;
  const at = Date.parse(finished.finishedAt);
  if (Number.isNaN(at) || now - at > RECENTLY_FINISHED_MS) return null;
  if (finished.state === "briefed" || finished.state === "done") return { kind: "ready", entry: finished };
  if (finished.state === "failed") return { kind: "failed", entry: finished };
  return null;
}

/** The badge's words. Pure; exported for tests. */
export function queueBadgeText(status: QueueBadgeStatus): string {
  switch (status.kind) {
    case "queued":
      return `Queued · #${String(status.position)}`;
    case "running": {
      const phase = status.entry.phase ? RUN_PHASE_LABELS[status.entry.phase] : null;
      return phase ? `Running · ${phase}` : "Running";
    }
    case "ready":
      // Never a brief state: the brief may have been posted or held back since it finished.
      if (status.entry.kind === "groom-item") return FINISHED_TEXT;
      return status.entry.kind === "author-questions" ? "Questions drafted" : "Draft ready";
    case "failed":
      return "Failed";
  }
}

const TONE: Record<QueueBadgeStatus["kind"], string> = {
  queued: "border-[#3F3F46] bg-[#18181B] text-[#D4D4D8]",
  running: "border-sky-500/30 bg-sky-500/[0.12] text-sky-300",
  ready: "border-green-500/25 bg-green-500/[0.12] text-green-400",
  failed: "border-red-500/30 bg-red-500/[0.12] text-red-300",
};

/** The badge for a known status: a link to the Queue screen. */
export function QueueBadge({ status, className }: { status: QueueBadgeStatus; className?: string | undefined }) {
  const text = queueBadgeText(status);
  const Icon = status.kind === "running" ? Loader2 : status.kind === "queued" ? Clock : status.kind === "ready" ? CheckCircle2 : XCircle;
  return (
    <Link
      to={GROOMING_QUEUE_PATH}
      className={cn(
        "inline-flex h-[22px] max-w-full items-center gap-1 whitespace-nowrap rounded-full border px-2 text-[11px] font-medium hover:underline",
        TONE[status.kind],
        className
      )}
      title={`${text} · open the queue`}
      data-testid="queue-status-badge"
      data-kind={status.kind}
      onClick={(event) => {
        event.stopPropagation();
      }}
    >
      <Icon className={cn("h-3 w-3 flex-none", status.kind === "running" && "motion-safe:animate-spin")} aria-hidden="true" />
      <span className="truncate">{text}</span>
    </Link>
  );
}

/** The queue status of one key on this computer, or null (none, a browser, or a contract-2 app). */
export function useQueueBadgeStatus(key: string | null): QueueBadgeStatus | null {
  const { state } = useGroomingQueue();
  return queueBadgeStatus(state, key, Date.now());
}

export interface QueueStatusBadgeProps {
  project: string;
  /** The tracker item; omit for a product-areas draft. */
  itemId?: string | null;
  /** What the entry runs; a brief (`groom-item`) by default. */
  kind?: GroomingQueueEntryKind;
  className?: string;
}

/** The badge for an item (or a project's product areas); renders nothing when it is not in the queue. */
export function QueueStatusBadge({ project, itemId, kind = "groom-item", className }: QueueStatusBadgeProps) {
  const status = useQueueBadgeStatus(safeQueueKey(kind, project, itemId));
  return status ? <QueueBadge status={status} className={className} /> : null;
}

// ---------------------------------------------------------------------------
// Adding to the queue
// ---------------------------------------------------------------------------

/** True while every run slot is taken or the queue is paused, so a new entry waits. Pure; exported for tests. */
export function queueIsBusy(state: GroomingQueueState | null): boolean {
  if (!state) return false;
  return state.status === "paused" || state.running.length >= state.concurrency;
}

/** "Next in line" for an entry the app put there, else "#3": an entry's place in Up next. */
function placeWords(entry: GroomingQueueEntry, index: number): string {
  return entry.nextInLine === true ? "Next in line" : `#${String(index + 1)}`;
}

/**
 * The toast after an enqueue, or null when none is needed. Pure; exported
 * for tests.
 *
 * `after` is the queue once it shows the entry the enqueue named
 * (`result.entryId`): the toast then says the entry's real place, "Next in
 * line" when the app put a single item there (epic PI-SPEC-SW-E290, UX-PM-04)
 * or "Added to the queue (#n)". Without an entry id (an older app) it falls
 * back to the end of Up next as `before` showed it. An entry already queued
 * says so, with its place when `after` shows it.
 */
export function enqueueToastText(
  result: GroomingQueueActionResult,
  before: GroomingQueueState | null,
  after: GroomingQueueState | null = null
): string | null {
  if (!result.ok) return null;
  const index = result.entryId && after ? after.upNext.findIndex((entry) => entry.id === result.entryId) : -1;
  const entry = index >= 0 ? after?.upNext[index] : undefined;
  if (result.alreadyQueued) {
    return entry ? `Already in the queue (${entry.nextInLine === true ? "next in line" : `#${String(index + 1)}`}).` : "Already in the queue.";
  }
  if (!queueIsBusy(before)) return null;
  if (entry) return entry.nextInLine === true ? "Next in line: it starts when a run finishes." : `Added to the queue (${placeWords(entry, index)})`;
  return `Added to the queue (#${String((before?.upNext.length ?? 0) + 1)})`;
}

/**
 * True when the toast for `result` should wait for a queue that shows the
 * entry: the app added an entry while the queue was busy, and the current
 * queue does not show it yet. Pure; exported for tests.
 */
export function enqueueToastWaitsForQueue(
  result: GroomingQueueActionResult,
  before: GroomingQueueState | null,
  now: GroomingQueueState | null
): boolean {
  if (!result.ok || !result.entryId || result.alreadyQueued === true) return false;
  if (!queueIsBusy(before)) return false;
  return !now || !now.upNext.some((entry) => entry.id === result.entryId);
}

export interface QueueStart {
  /** True when this page may add to the queue: a contract-3 app inside the supported range. */
  available: boolean;
  /** The entry's place when it is already queued or running (the start control hides then). */
  active: Extract<QueueBadgeStatus, { kind: "queued" | "running" }> | null;
  /** True while the app answers the enqueue. */
  pending: boolean;
  /** The plain failure of the last enqueue, or null. */
  error: string | null;
  /** Add to the queue; resolves with the app's answer. Shows "Next in line" or "Added to the queue (#n)" when it waits. */
  enqueue: () => Promise<GroomingQueueActionResult | null>;
}

/**
 * Call `onSettled` once when an entry that was queued or running leaves the
 * queue (it finished, failed, was stopped or removed), so the page re-reads
 * what the run saved in SpecTree.
 */
export function useOnQueueSettled(activeEntryId: string | null, onSettled: (() => void) | undefined): void {
  const previous = useRef<string | null>(null);
  const callback = useRef(onSettled);
  useEffect(() => {
    callback.current = onSettled;
  }, [onSettled]);
  useEffect(() => {
    if (previous.current !== null && activeEntryId === null) callback.current?.();
    previous.current = activeEntryId;
  }, [activeEntryId]);
}

/**
 * Start control state for one queue entry: whether the app has a queue,
 * whether the entry is already in it, and the enqueue call (`run` is the
 * bound bridge action, such as `actions.enqueueItem(project, itemId)`).
 * `onSettled` runs when the entry leaves the queue.
 */
export function useQueueStart(
  key: string | null,
  run: () => Promise<GroomingQueueActionResult>,
  onSettled?: () => void
): QueueStart {
  const { runner } = useRunnerBridge();
  const { supported, state } = useGroomingQueue();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const available = supported && runner !== null && key !== null;
  const active = activeEntryByKey(state, key);
  useOnQueueSettled(active?.entry.id ?? null, onSettled);

  // The latest queue, for the toast once the app's answer is back.
  const latest = useRef(state);
  latest.current = state;
  // A toast that waits for a queue that shows its entry (the app answers before it reports the queue).
  const waitingToast = useRef<{ result: GroomingQueueActionResult; before: GroomingQueueState | null } | null>(null);
  useEffect(() => {
    const waiting = waitingToast.current;
    if (!waiting || !waiting.result.ok || !state) return;
    const { entryId } = waiting.result;
    const shows = (entries: GroomingQueueEntry[]) => entries.some((entry) => entry.id === entryId);
    const waits = shows(state.upNext);
    // Not reported yet: keep waiting. Already running or finished: no place to name.
    if (!waits && !shows(state.running) && !shows(state.completed)) return;
    waitingToast.current = null;
    if (!waits) return;
    const toast = enqueueToastText(waiting.result, waiting.before, state);
    if (toast) showGroomingToast(toast, "info");
  }, [state]);

  const enqueue = async (): Promise<GroomingQueueActionResult | null> => {
    if (!available || pending) return null;
    const before = state;
    setPending(true);
    setError(null);
    try {
      const result = await run();
      if (!result.ok) {
        setError(result.message);
      } else if (enqueueToastWaitsForQueue(result, before, latest.current)) {
        waitingToast.current = { result, before };
      } else {
        const toast = enqueueToastText(result, before, latest.current);
        if (toast) showGroomingToast(toast, "info");
      }
      return result;
    } finally {
      setPending(false);
    }
  };

  return { available, active, pending, error, enqueue };
}

/** {@link useQueueStart} for a brief of one tracker item ("Groom this item"). */
export function useEnqueueItem(project: string, itemId: string, onSettled?: () => void): QueueStart {
  const { actions } = useGroomingQueue();
  return useQueueStart(safeQueueKey("groom-item", project, itemId), () => actions.enqueueItem(project, itemId), onSettled);
}
